import { useTranslation } from "@/packages/utils";
import { useBilling } from "@/packages/features-billing";
import { CustomerPortalLink } from "@convex-dev/polar/react";
import { api } from "convex/_generated/api";
import {
  SettingsPageHeader,
  SettingsPanel,
  SettingsSection,
} from "./SettingsSection";
import { PlanCard } from "./PlanCard";
import { PricingTable } from "./PricingTable";
import { InvoiceSection } from "./InvoiceSection";
import { BillingSkeletonLoader } from "./BillingSkeletonLoader";
import { PastDueBanner } from "./PastDueBanner";
import { BillingErrorBoundary } from "./BillingErrorBoundary";
import { Button } from "@/components/ui";

export function BillingSection() {
  const { t } = useTranslation(["settings"]);
  const { tier, subscription, isLoading } = useBilling();

  if (isLoading) return <BillingSkeletonLoader />;

  const billingInterval = subscription?.billingInterval ?? null;

  return (
    <BillingErrorBoundary>
      {subscription?.status === "past_due" && <PastDueBanner />}

      <div className="flex flex-col gap-8">
        <SettingsPageHeader
          eyebrow={t("settings:sections.account")}
          title={t("settings:billing.title")}
          description={t("settings:billing.description")}
        />
        <SettingsPanel>
          <PlanCard subscription={subscription ?? null} tier={tier} />
        </SettingsPanel>

        <SettingsSection title={t("settings:billing.planComparison")}>
          <PricingTable currentTier={tier} billingInterval={billingInterval} />
        </SettingsSection>

        <SettingsSection title={t("settings:billing.paymentMethod")}>
          <PaymentMethodSection />
        </SettingsSection>

        <SettingsSection title={t("settings:billing.invoiceHistory")}>
          <InvoiceSection />
        </SettingsSection>
      </div>
    </BillingErrorBoundary>
  );
}

function PaymentMethodSection() {
  const { t } = useTranslation(["settings"]);
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-page">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-secondary"
          >
            <title>{t("settings:billing.paymentMethodIconTitle")}</title>
            <rect width="20" height="14" x="2" y="5" rx="2" />
            <line x1="2" x2="22" y1="10" y2="10" />
          </svg>
        </div>
        <div>
          <p className="text-[14px] font-medium text-primary">
            {t("settings:billing.managedByPolar")}
          </p>
          <p className="text-[12px] text-secondary mt-0.5">
            {t("settings:billing.managedByPolarDescription")}
          </p>
        </div>
      </div>
      <CustomerPortalLink
        polarApi={{
          generateCustomerPortalUrl: api.polar.generateCustomerPortalUrl,
        }}
      >
        <Button variant="secondary" size="sm">
          {t("settings:billing.updatePaymentMethod")}
        </Button>
      </CustomerPortalLink>
    </div>
  );
}
